import { ChangeEvent, useState } from 'react';
import { Button } from '@upmc/ims.component-library';

import axiosInstance from '../../services/axios/axios';
import Edit from './containers/Edit';
import FormEntry from './components/FormEntry';
import SubmissionSummary from './containers/SubmissionSummary';
import UARLayout from './containers/Layout';
import {
	TextboxTypes,
	DropdownTypes,
} from '../../components/UI/Input';
import { Headline6 } from '../../components/UI/MDC/Typography/Typography';
import { WrapImsPacket } from '../../store/utility';
import * as actions from '../../store/ancillary/actions'
import * as utils from '../../utils';
import { SUBMISSION_LOADING_TITLE, UAR_CONSTANTS } from './util/constants';
import Icon from '../../components/UI/MDC/Icon/Icon';

/**
 * User Access Review Terminate Component
 * @param {object} props
 */
const Terminate = props => {
	/**
	 * Extract needed variables from the props provided by the `Scaffold`
	 */
	const {
		dispatch,
		loggedInUser,
		error,
		errorMessage,
		loading,
		profileDetails,
		bulkSelections,
		canPerformActions,
	} = props;

	//STATE

	/**
	 * (set)Submitted - flag to indicate if submit button has been clicked
	 */
	const [submitted, setSubmitted] = useState(false);
	/**
	 * (set)SubmittedSuccessfully - flag to indicate if submission was successful
	 */
	const [submittedSuccessfully, setSubmittedSuccessfully] = useState(false);

	/**
	 * (set)ShowHelp - display additional help information
	 */
	const [showHelp, setShowHelp] = useState(false);

	/**
	 * set(Form) - data for input form
	 */
	const [form, setForm] = useState({
		terminationTime: {
			label: 'Termination Time',
			value: 'ASAP',
			options: [
				{
					key: 'asap',
					value: 'ASAP',
				},
				{
					key: 'future',
					value: 'Future Date',
				},
			],
			visible: true,
			validation: {
				valid: true,
				message: '',
				rules: {
					required: true,
				},
				touched: false,
			},
			settings: {
				readOnly: false,
				type: DropdownTypes.DROPDOWN,
			},
		},
		terminationDate: {
			label: 'Termination Date',
			value: '',
			options: null,
			visible: false,
			validation: {
				valid: true,
				message: '',
				rules: {
					required: true,
				},
				touched: false,
			},
			settings: {
				readOnly: false,
				type: TextboxTypes.DATE,
			},
		},
		emailList: {
			label: 'Send email copy',
			value: '',
			options: null,
			visible: true,
			validation: {
				valid: true,
				message: '',
				rules: {
					required: false,
				},
				touched: false,
			},
			settings: {
				readOnly: false,
				type: TextboxTypes.TEXT,
				placeholder: 'email addresses separated by semi-colons (;)',
			},
		},
	});

	//FUNCTIONS
	/**
	 * event handler for form input changes
	 * @param {ChangeEvent<HTMLInputElement>} event
	 * @param {string} key
	 */
	const handleChange = (event, key) => {
		if (key in form) {
			let newForm = { ...form };
			newForm[key].value = event.target.value;

			if (key === 'terminationTime') {
				const visibility = newForm[key].value === 'Future Date';
				newForm['terminationDate'].visible = visibility;
			}
			setForm(newForm);
		}
	};

	/**
	 * actions to take upon form submission
	 */
	const submitHandler = () => {
		const isValid = validate();
		if (isValid) {
			let targets = [];
			if (bulkSelections && bulkSelections.length > 0) {
				targets = bulkSelections;
			} else {
				targets.push(profileDetails);
			}
			const payload = {
				loggedInUser: loggedInUser.loggedInUser,
				terminationDate: form.terminationDate.value,
				terminationTime: form.terminationTime.value,
				emailList: form.emailList.value,
				targets: targets,
			};

			setSubmitted(true);
			axiosInstance
				.post(
					'/api/UserAccessReview/Termination',
					WrapImsPacket(payload)
				)
				.then(response => {
					switch (response.status) {
						case 200:
							if (response.data.responseCode) {
								const responseCode = response.data.responseCode;
								switch (responseCode) {
									case 200:
										setSubmittedSuccessfully(true);
										break;
									default:
										setSubmitted(false);
										setSubmittedSuccessfully(false);
										break;
								}
							}
							break;

						default:
							setSubmitted(false);
							setSubmittedSuccessfully(false);
							break;
					}
				})
				.catch(errors => {
					setSubmitted(false);
					setSubmittedSuccessfully(false);
				});
		} else {
			dispatch(
				actions.addMessage({
					Key: 2,
					Value: 'Please fix validation issues below.',
				})
			);
		}
	};

	/**
	 * actions to take if cancelled
	 */
	const cancelHandler = () => {
		props.history.goBack();
	};

	/**
	 * form validation logic
	 * @param {ChangeEvent<HTMLInputElement>} event
	 */
	const validate = event => {
		const touchedKey =
			event && event.target && event.target.id ? event.target.id : null;
		let newForm = { ...form };
		let allValid = true;
		for (let key in newForm) {
			let valid = false;
			let value = newForm[key].value;
			let message = '';
			switch (key) {
				case 'terminationTime':
					valid =
						value.toLowerCase() === 'asap' ||
						value.toLowerCase() === 'future date';
					if (!valid) {
						message = 'Please select a valid option';
					}
					break;
				case 'terminationDate':
					if (newForm[key].visible) {
						// utils.prettyDateFormat('');
						const isEmpty = value ? false : true;
						const isDate = utils.validateDateString(value);
						const isFuture =
							isDate && utils.isDateStringInFuture(value);
						valid = !isEmpty && isDate && isFuture;

						if (!isFuture) {
							message =
								'The date provided is not in the future.  Please provide a future date or use the "ASAP option."';
						}
						if (!isDate) {
							message = 'The value provided is not a valid date.';
						}
						if (isEmpty) {
							message = 'Please provide a valid future date.';
						}
					} else {
						valid = true;
					}
					break;
				case 'emailList':
					if (value) {
						const emails = value.split(';');
						emails.forEach(email => {
							valid = utils.isValidEmail(email);
						});
						if (!valid) {
							message =
								'One or more of the specified email addresses you provided is invalid.';
						}
					} else {
						valid = true;
					}
					break;
				default:
					break;
			}
			newForm[key].validation.valid = valid;
			newForm[key].validation.message = message;

			//no `touchedKey` indicates a submission event
			if (!touchedKey) {
				newForm[key].validation.touched = true;
			}
			allValid = allValid && valid;
		}

		//presence of `touchedKey` indicates an input focus-change event
		if (touchedKey) {
			newForm[touchedKey].validation.touched = true;
		}

		setForm(newForm);
		return allValid;
	};

	//RENDER
	/**
	 * Retrieve settings from state and generate the input component
	 * @param {string} key
	 */
	const generateFormInput = key => {
		let formInput = null;
		if (key in form && form[key].visible) {
			formInput = (
				<FormEntry
					entryKey={key}
					formDetails={form[key]}
					handleChange={e => handleChange(e, key)}
					validateChange={validate}
					inputType={
						form[key].settings && form[key].settings.type
							? form[key].settings.type
							: 'text'
					}
					readOnly={
						(form[key].settings && form[key].settings.readOnly) ||
						submittedSuccessfully
					}
					placeholder={
						form[key].settings && form[key].settings.placeholder
							? form[key].settings.placeholder
							: null
					}
				/>
			);
		}
		return formInput;
	};

	/**
	 * Retrieve settings from state and generate the summary
	 * @param {string} key
	 */
	const generateFormSummary = key => {
		let formInput = null;
		if (key in form && form[key].visible) {
			formInput = (
				<FormEntry
					entryKey={key}
					formDetails={form[key]}
					handleChange={e => handleChange(e, key)}
					validateChange={validate}
					inputType={'SUMMARY'}
					readOnly={true}
					value={form[key].value}
				/>
			);
		}
		return formInput;
	};

	const employeeTerminationNotice = () => {
		const infoEmployee = `For Employee Terminations:  Please be advised that terminating a user's UPMC E-Sign-On account and associated applications through this function in IMS will not terminate the user in the HR Direct System.  If you have already done so, please terminate the user through HR Direct.  You only need to terminate an employee through this option if you need to revoke their access ASAP.  If you only terminate the user through IMS and not HR Direct, the employee account will reactivate the next day in IMS.`;
		const infoNonEmployee = `For Non-Employee Terminations:  Please be advised that terminating a user's UPMC E-Sign-On account and associated applications through this function will terminate the user in full.`;
		const infoServiceAccounts = `For Service Account Terminations: Please be advised that when terminating the Primary Account of a user, Admin Service Accounts will be terminated at the same time as well.  If the terminated user has other service accounts reporting to them when the Primary Account is terminated, the other service account(s) will automatically begin reporting to the terminated user's manager.  Please note that you can terminate individual service accounts, including admin accounts by checking the service account checkbox.`;
		return (
			<div>
				<Headline6>Please Note: {infoEmployee}</Headline6>
				<Button
					variant='text'
					onClick={() => setShowHelp(!showHelp)}
					endIcon={
						<Icon
							icon={
								showHelp
									? 'unfold_less'
									: 'unfold_more'
							}
						/>
					}
				>
					{showHelp ? 'Read Less' : 'Read More'}
				</Button>
				{showHelp ? (
					<ul>
						<li>{infoEmployee}</li>
						<li>{infoNonEmployee}</li>
						<li>{infoServiceAccounts}</li>
					</ul>
				) : null}
			</div>
		);
	};

	/**
	 * Form components
	 */
	const formOutput = (
		<>
			{profileDetails &&
			profileDetails.type === UAR_CONSTANTS.EMPLOYEE_KEY
				? employeeTerminationNotice()
				: null}

			{/* Termination Time */}
			{generateFormInput('terminationTime')}

			{/* Termination Date */}
			{generateFormInput('terminationDate')}

			{/* Email List */}
			{generateFormInput('emailList')}
		</>
	);

	/**
	 * Form summary components
	 */
	const formOutputSubmitted = (
		<>
			{profileDetails &&
			profileDetails.type === UAR_CONSTANTS.EMPLOYEE_KEY
				? employeeTerminationNotice()
				: null}

			{/* Termination Time */}
			{generateFormSummary('terminationTime')}

			{/* Termination Date */}
			{generateFormSummary('terminationDate')}

			{/* Email List */}
			{generateFormSummary('emailList')}
		</>
	);

	/**
	 * return JSX to render
	 */
	return (
		<UARLayout
			title='Terminate'
			loading={loading}
			error={error}
			errorMessage={errorMessage}
			canPerformActions={canPerformActions}
		>
			{submittedSuccessfully ? (
				<SubmissionSummary
					profile={
						bulkSelections && bulkSelections.length > 0
							? null
							: profileDetails
					}
				>
					{formOutputSubmitted}
				</SubmissionSummary>
			) : (
				<Edit
					cancellable={{
						handler: cancelHandler,
						disabled: submitted,
					}}
					submittable={{
						handler: submitHandler,
						label: 'Confirm Termination',
						disabled: submitted,
					}}
					loading={{
						isLoading: submitted,
						title: SUBMISSION_LOADING_TITLE,
						message: null,
					}}
					profile={
						bulkSelections && bulkSelections.length > 0
							? null
							: profileDetails
					}
				>
					{formOutput}
				</Edit>
			)}
		</UARLayout>
	);
};

export default Terminate;
